import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.tsx";
import { Persona } from "@/api/personas.ts";
import {
  formatWorldviewText,
  WorldviewModal,
} from "@/components/modals/WorldviewModal.tsx";
import {
  ContentStrategyModal,
  formatContentStrategyText,
} from "@/components/modals/ContentStrategyModal.tsx";
import { CustomRulesModal } from "@/components/modals/CustomRulesModal.tsx";
import { Role } from "@/components/AuthorizationProvider.tsx";
import { Button } from "@/components/ui/button.tsx";
import { PropsWithChildren, useRef, useState } from "react";
import { useAuth } from "@/hooks/use-auth.tsx";
import { DialogTrigger } from "@/components/ui/dialog";

export function AgentInfoTab({ persona }: { persona: Persona }) {
  const { hasAtLeastRole, authenticated } = useAuth();
  const editPersonaDisabled =
    !persona.persona_id ||
    (persona.public_persona === true && !hasAtLeastRole(Role.TenantAdmin));

  return (
    <div className="flex flex-col gap-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Bio</CardTitle>
        </CardHeader>

        <CardContent>
          <Collapsible>
            <p className="text-sm text-muted-foreground">{persona.bio}</p>
          </Collapsible>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row gap-x-2 justify-between items-center">
          <CardTitle>Worldview</CardTitle>
          {authenticated && !editPersonaDisabled && (
            <WorldviewModal
              personaId={persona.persona_id ?? ""}
              personaData={persona}
              authenticated={authenticated}
            />
          )}
        </CardHeader>

        <CardContent>
          <Collapsible>
            <p className="text-sm text-muted-foreground">
              {persona.persona_worldview
                ? formatWorldviewText(persona.persona_worldview)
                : null}
            </p>
          </Collapsible>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row gap-x-2 justify-between items-center">
          <CardTitle>Content Strategy</CardTitle>
          {authenticated && !editPersonaDisabled && (
            <ContentStrategyModal
              personaId={persona.persona_id ?? ""}
              personaData={persona}
              authenticated={authenticated}
            />
          )}
        </CardHeader>

        <CardContent>
          <Collapsible>
            <p className="text-sm text-muted-foreground">
              {persona.content_strategy
                ? formatContentStrategyText(persona.content_strategy)
                : null}
            </p>
          </Collapsible>
        </CardContent>
      </Card>

      {authenticated && !editPersonaDisabled && (
        <Card>
          <CardHeader className="flex flex-row gap-x-2 justify-between items-center">
            <CardTitle>Custom Rules</CardTitle>
            <CustomRulesModal
              personaId={persona.persona_id ?? ""}
              personaData={persona}
            >
              <DialogTrigger asChild>
                <Button size="sm" variant="outline">
                  Edit
                </Button>
              </DialogTrigger>
            </CustomRulesModal>
          </CardHeader>

          <CardContent>
            <Collapsible>
              <p className="text-sm text-muted-foreground">
                {persona.custom_prompt_rules}
              </p>
            </Collapsible>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Collapsible({
  children,
  maxHeight = 200,
}: PropsWithChildren<{ maxHeight?: number }>) {
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const resizeObserverRef = useRef(
    new ResizeObserver((entries) => {
      const entry = entries[0];

      setCanExpand(entry.contentRect.height > maxHeight);
    }),
  );

  const observeSize = (ref: HTMLDivElement | null) => {
    if (ref) {
      resizeObserverRef.current.observe(ref);
    } else {
      resizeObserverRef.current.disconnect();
    }
  };

  return (
    <div className="relative">
      <div
        className="overflow-y-hidden"
        style={{ maxHeight: expanded ? undefined : `${maxHeight}px` }}
      >
        <div ref={observeSize} className={canExpand ? "pb-6" : ""}>
          {children}
        </div>
      </div>
      {canExpand ? (
        <>
          {expanded ? null : (
            <div className="absolute w-full h-36 bottom-0 bg-gradient-to-b from-transparent to-[#161618]" />
          )}
          <Button
            className="absolute -bottom-4 -left-4"
            variant="ghost"
            onClick={() => setExpanded((expanded) => !expanded)}
          >
            {expanded ? "See less" : "See more"}
          </Button>
        </>
      ) : null}
    </div>
  );
}
